import _ from 'lodash';
import type { AxiosError } from 'axios';
import { captureException } from '@sentry/nextjs';

import httpClient, { getLanguageHeader } from 'src/http-client';
import { parseCity, parsePrefecture } from 'utils/data-transform/location';
import type { PublicRequest } from 'modules/requests/service';
import type { City, Location } from 'utils/data-transform/location';
import type { State } from './duck';
import type {
  BlogPost,
  LandingPagePro,
  CaseStudy,
  ProBlogPost,
  ListProsRequestParams,
  ProJobCategoriesForSignup,
} from './marketing-page.types';
import type { ProCardProProfile } from 'modules/marketing-pages/components/ListPros/ProCard';
import {
  NUM_NEARBY_CITIES_PROS_PER_PAGE,
  NUM_NEARBY_CITIES_PROS_PER_PAGE_OVER_PAGE2,
} from 'constants/vertical-lp';
function formatPros(
  pros: Array<Omit<LandingPagePro, 'page'>>,
  language: string,
  page: number
): Array<LandingPagePro> {
  return pros.map((pro) => {
    if (_.has(pro, 'location.city.key') && _.has(pro, 'location.prefecture.key')) {
      return {
        ...pro,
        location: {
          ...(pro.location ?? {}),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          city: parseCity(pro.location?.city as City, language),
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          prefecture: parsePrefecture(pro.location?.prefecture as Location, language),
          title: pro.location?.title ?? '',
        },
        page,
      };
    }

    return { ...pro, page };
  });
}

export async function listBlogPosts(language = 'ja') {
  const { data } = await httpClient.get<Array<BlogPost>>('/blog-posts', {
    headers: getLanguageHeader(language),
  });
  return data;
}

export async function listBlogPostsByCategoryId(categoryId: string) {
  const { data } = await httpClient.get<Array<BlogPost>>(`/blog-posts/${categoryId}`);
  return data;
}

export async function listProBlogPostsByJobTypeId(jobTypeId: string) {
  const { data } = await httpClient.get<Array<ProBlogPost>>(
    `/pro-blog-posts/job-type/${jobTypeId}`
  );
  return data;
}

export async function getProJobCategoriesForSignUpTree() {
  // 11/18/2022: z-api only looks for existence of path as a query param to do tree minimization
  const { data } = await httpClient.get<ProJobCategoriesForSignup>(
    'pro-job-categories-for-signup/tree?path=initial'
  );
  return data;
}

export async function listPros({
  cityGroup,
  cityKey,
  day,
  excludeCities,
  jobTypeId,
  language,
  matchingCriteria,
  page,
  prefectureKey,
  priceRange,
  pricingMenuItemKey,
  size,
  skip,
  time,
}: {
  cityGroup?: string;
  cityKey?: string;
  day?: string;
  excludeCities?: Array<string>;
  jobTypeId: string;
  language: string;
  matchingCriteria?: string;
  page: number;
  prefectureKey?: string;
  priceRange?: string;
  pricingMenuItemKey?: string;
  size: number;
  skip?: number;
  time?: string;
}) {
  let listUrl = '/pro-location-jobtype-ref/pros-by-job';
  let pageSize = size;
  let pageSkip = skip;
  if (size === NUM_NEARBY_CITIES_PROS_PER_PAGE) {
    if (page > 1) {
      pageSize = NUM_NEARBY_CITIES_PROS_PER_PAGE_OVER_PAGE2;
    }
    if (page > 1) {
      pageSkip =
        NUM_NEARBY_CITIES_PROS_PER_PAGE + NUM_NEARBY_CITIES_PROS_PER_PAGE_OVER_PAGE2 * (page - 2);
    }
  }

  const params: ListProsRequestParams = {
    day,
    jobType: jobTypeId,
    language: language as 'en' | 'ja',
    matchingCriteriaFilter:
      (cityKey || prefectureKey) && matchingCriteria ? matchingCriteria : undefined,
    priceRange: priceRange ? priceRange : undefined,
    pricingMenuItemKey: pricingMenuItemKey ? pricingMenuItemKey : undefined,
    size: pageSize,
    skip: pageSkip,
    time,
  };

  if (cityGroup && cityKey) {
    listUrl = '/pro-location-jobtype-ref/pros-by-job-prefecture-and-city-not-nearby-cities';
    params.city = cityKey;
    params.prefecture = prefectureKey;
  } else if (cityGroup && !cityKey) {
    listUrl = '/pro-location-jobtype-ref/pros-by-job-and-city-group';
    params.cityGroup = cityGroup;
    params.excludeCities = excludeCities;
  } else if (cityKey) {
    listUrl = '/pro-location-jobtype-ref/pros-by-job-prefecture-and-city';
    params.city = cityKey;
    params.prefecture = prefectureKey;
  } else if (prefectureKey) {
    listUrl = '/pro-location-jobtype-ref/pros-by-prefecture-and-job';
    params.prefecture = prefectureKey;
  }

  const {
    data: { pros, totalPros, proReviewStatistics = null },
  } = await httpClient.get<{
    proReviewStatistics: { ratingValue: number; reviewCount: number } | null;
    pros: Array<Omit<LandingPagePro, 'page'>>;
    totalPros: number;
  }>(listUrl, {
    headers: getLanguageHeader(language),
    params,
  });

  const formattedPros = formatPros(pros, language, page);
  return {
    proReviewStatistics,
    pros: formattedPros,
    total: totalPros,
  };
}

export async function getNewestReviewsByJobType({ jobTypeId }: { jobTypeId: string }) {
  const response = await httpClient.get<State['newestReviews']>(
    `/pro-feedbacks/get-newest-by-jobtype/${jobTypeId}`
  );

  return Array.isArray(response.data) ? response.data : [];
}

export async function getProCount({
  cityKey,
  jobTypeId,
  language,
  prefectureKey,
}: {
  cityKey?: string;
  jobTypeId: string;
  language?: string;
  prefectureKey?: string;
}) {
  const count = await httpClient
    .get('/pro-location-jobtype-ref/pros-count', {
      params: { cityKey, jobTypeId, language, prefectureKey },
    })
    .then((res) => (typeof res.data === 'number' ? res.data : 0));
  return { count };
}

export async function listSampleRequests({
  cityId,
  jobTypeId,
  language,
  prefectureId,
  limit,
}: {
  cityId?: string;
  jobTypeId: string;
  language: string;
  limit?: number;
  prefectureId?: string;
}) {
  const response = await httpClient.get<State['sampleRequests']>('/sample-requests/query', {
    headers: getLanguageHeader(language),
    params: { cityId, jobTypeId, language, limit, prefectureId },
  });
  return Array.isArray(response.data) ? response.data : [];
}

export async function trackUserEvent({
  event,
  properties,
  type,
}: {
  event: string;
  properties: Record<string, any>;
  type: string;
}) {
  await httpClient.post('/events', { event, meta: { timestamp: Date.now() }, properties, type });
}

export async function getPopularPricingItems({
  cityKey,
  jobTypeId,
  language = 'ja',
  prefectureKey,
}: {
  cityKey?: string;
  jobTypeId: string;
  language?: string;
  prefectureKey?: string;
}) {
  const response = await httpClient.get<{
    popularItems: State['popularPricingItems'];
  }>('/pricing-menu/popular-items', { params: { cityKey, jobTypeId, language, prefectureKey } });
  return response.data.popularItems;
}

export async function getCaseStudies({
  jobTypeId,
  language,
  currentPage,
  pageSize,
}: {
  currentPage: number;
  jobTypeId: string;
  language: string;
  pageSize: number;
}) {
  const {
    data: { caseStudies, totalCaseStudies },
  } = await httpClient.get<{
    caseStudies: Array<CaseStudy>;
    totalCaseStudies: number;
  }>(`case-studies/job-type/${jobTypeId}`, {
    headers: getLanguageHeader(language),
    params: {
      currentPage,
      pageSize,
    },
  });

  return {
    caseStudies: caseStudies.map((caseStudy) => ({ ...caseStudy, page: currentPage })),
    totalCaseStudies,
  };
}

/**
 * =============================
 * Start Popular Request Section
 * =============================
 */

export interface PopularRequestSampleApply {
  coverLetter: string;
  proProfile: ProCardProProfile;
}

export interface PopularRequest {
  id: string;
  request: PublicRequest;
  sampleApplyInfo: PopularRequestSampleApply;
}

export async function getPopularRequests({
  cityKey,
  jobTypeId,
  prefectureKey,
}: {
  cityKey: string | undefined;
  jobTypeId: string;
  prefectureKey: string | undefined;
}) {
  try {
    const { data: popularRequests } = await httpClient.get<Array<PopularRequest>>(
      `requests/popular-requests/`,
      {
        params: {
          cityKey,
          jobTypeId,
          prefectureKey,
        },
      }
    );

    return popularRequests;
  } catch (error) {
    return [];
  }
}

/**
 * ===========================
 * End Popular Request Section
 * ===========================
 */

export interface InternalBlogPostApiType {
  authorDescription: string;
  authorImageUrl: string;
  authorName: string;
  content: string;
  createdTime: number;
  excerpt: string;
  featuredImageUrl: string;
  jobTypeId: string;
  layoutTemplate: string;
  metaDescription: string;
  name: string;
  title: string;
  updatedTime: number;
}

type GetBlogByPostNameParams = {
  blogPostName: string;
};
export const getInternalBlogPostByPostName = async ({ blogPostName }: GetBlogByPostNameParams) => {
  let internalBlog;
  try {
    ({ data: internalBlog } = await httpClient.get<InternalBlogPostApiType>(
      `/internal-blog-posts/${blogPostName}`
    ));
  } catch (error) {
    captureException(error);
    const axiosError = error as AxiosError;
    // eslint-disable-next-line no-magic-numbers
    if (axiosError.response?.status === 404) {
      return { notFound: true };
    }
    return Promise.reject(error);
  }

  const { content: htmlContent, ...internalBlogData } = internalBlog;
  // internal blog posts are pulled from wordpress, submitting a featuredImageUrl is optional
  // when not submitted in WP, this field in NULL and we pull this value as a string into the DB
  // NULL as a string does not represent a proper image url so return the primitive null.
  const featuredImageUrl =
    internalBlog.featuredImageUrl.toLowerCase() === 'null' ? null : internalBlog.featuredImageUrl;
  const transformedInternalBlog = {
    ...internalBlogData,
    featuredImageUrl,
    // alias content as htmlContent to communicate it can be dangerously rendered
    htmlContent,
  };
  return transformedInternalBlog;
};

interface GetBlogPostByJobTypeIdParams {
  jobTypeId: string;
  options?: { blogPostNameToFilter?: string };
}
export const getInternalBlogPostsByJobTypeId = async ({
  jobTypeId,
  options: { blogPostNameToFilter } = {},
}: GetBlogPostByJobTypeIdParams): Promise<Array<BlogPost>> => {
  let internalJobTypeRelatedBlogPosts: Array<BlogPost>;
  try {
    ({ data: internalJobTypeRelatedBlogPosts } = await httpClient.get<Array<BlogPost>>(
      `/internal-blog-posts/job-type/${jobTypeId}`
    ));
  } catch (error) {
    captureException(error);
    return [];
  }

  return blogPostNameToFilter
    ? internalJobTypeRelatedBlogPosts.filter(({ name }) => name !== blogPostNameToFilter)
    : internalJobTypeRelatedBlogPosts;
};

interface GetBlogPostByCategoryIdParams {
  categoryId: string;
}
export const getInternalBlogPostsByCategoryId = async ({
  categoryId,
}: GetBlogPostByCategoryIdParams): Promise<Array<BlogPost>> => {
  let internalJobTypeRelatedBlogPosts: Array<BlogPost>;
  try {
    ({ data: internalJobTypeRelatedBlogPosts } = await httpClient.get<Array<BlogPost>>(
      `/internal-blog-posts/category/${categoryId}`
    ));
  } catch (error) {
    captureException(error);
    return [];
  }

  return internalJobTypeRelatedBlogPosts;
};
